import { SetModularityConfig } from '../actions/actions-modularity';
import { Injectable, createComponent, EnvironmentInjector, Injector, Inject, Optional } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, tap, take, filter, startWith, catchError } from 'rxjs/operators';
import { BackendService } from '../services/backend.service';
import { ActionsTypes, InitGuestMenuList } from '../actions/actions';
import { ModularityActionTypes } from '../actions/actions-modularity';
import * as HomepageActions from '../actions/actions';
import {
  HeaderAlertService,
  CORE_ACTION,
  MENU_FILTERS,
  IMenuFilter,
  MenuActionTypes,
  CartWidgetsActionTypes,
  SetCartWidgetData,
  DirectLocaleService,
  customerConfig,
  WindowCommunicatorService,
  LoginService,
  Config as MConfig,
  LocaleMapperService,
  ConfigService,
  Locations
} from '@uxdf/direct-shared-lib';
import { AgreementBannerComponent } from '../../components/homepage/banners/agreement/agreement-banner.component';
import { Store, select } from '@ngrx/store';
import { State, getMenuListState, __config_contact, getCustomerConfig } from '../reducers';
import { combineLatest, of, zip } from 'rxjs';
import * as ModularityActions from '../actions/actions-modularity';
import { GetBonusPoints } from '../actions/loyalty/actions-bonus-points';
import { GetAccount, GET_ACCOUNT_ERROR, GET_ACCOUNT_SUCCESS } from '../actions/loyalty/actions-account';
import { REGISTER_AGENT_SUCCESS } from '../actions/loyalty/actions-agent';
import { Item, Menu } from '../models/menu';

import { Router } from '@angular/router';
import { PermissionHelper } from '../services/utils/permission-helper.service';
import { setLocaleAction } from '@uxdf/locale-manager';
import { BookMessageTypes, ParserMessageTypes } from '../../modules/parser/models/message.model';

@Injectable()
export class Effects {
  initCustomerConfig$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CORE_ACTION.INIT_CUSTOMER_CONFIG),
      switchMap(() => {

        return this.backendService.initCustomerConfig().pipe(
          map(backendConfig => {
            const preferredStore = this.localeMapperService.getPreferredStoreForCustomer(backendConfig);
            this.store.dispatch(setLocaleAction({ bcpLocale: preferredStore.locale }));
            const customerConfigFromBackend = this.localeMapperService.setPreferredLanguage(
              backendConfig,
              preferredStore
            );
            this.store
              .select(getCustomerConfig)
              .pipe(take(1))
              .subscribe(value => {
                customerConfigFromBackend.is_product_orderer = value.is_product_orderer;
                customerConfigFromBackend.is_financial_manager = value.is_financial_manager;
                customerConfigFromBackend.is_avc_dashboards = value.is_avc_dashboards;
                customerConfigFromBackend.is_avc_invoices = value.is_avc_invoices;
                customerConfigFromBackend.avc_ta_currency = value.avc_ta_currency;
                customerConfigFromBackend.is_avc_credit_line_status = value.is_avc_credit_line_status;
              });

            return new HomepageActions.SetupPreferredLangSuccess(customerConfigFromBackend);
          })
        );
      })
    )
  });

  setPreferredLanguageSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionsTypes.SetupPreferredLangSuccess),
      switchMap(action => [
        new HomepageActions.SetCustomerConfig({ customerConfig: action['payload'] }),
        new GetAccount(),
        new GetBonusPoints()
      ])
    )
  });

  initModularityConfig$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionsTypes.SetCustomerConfig),
      map(
        (action: HomepageActions.SetCustomerConfig) =>
         action.payload.customerConfig.country_code
      ),
      switchMap(() =>
        this.backendService
          .getModularityConfig()
          .pipe(map((modularityConfig: MConfig) => new ModularityActions.SetModularityConfig(modularityConfig)))
      )
    )
  });

  setupPermissions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ModularityActionTypes.SetModularityConfig),
      switchMap(action =>
        this.permissionHelper
          .setupPermissions(action['payload'])
          .pipe(map((data: any) => new HomepageActions.SetupPermissionsSuccess(data)))
      )
    )
  });

  /**
   * Retrieve data for menu filters and return filtered menu
   */
  initMenuList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionsTypes.SetupPermissionsSuccess, REGISTER_AGENT_SUCCESS),
      switchMap(() =>
        combineLatest([
          this.actions$.pipe(
            ofType(GET_ACCOUNT_SUCCESS, GET_ACCOUNT_ERROR),
            // start without loyalty and update when account info is loaded
            startWith(null)
          ),
          this.backendService.initMenuList()]
        ).pipe(
          switchMap(([_, menuList]) => {
            if (this.menuFilters) {
              // filter in place and emit when done
              return zip(...this.menuFilters.map(menuFilter => menuFilter.execute(menuList))).pipe(map(() => menuList));
            }
            return of(menuList);
          }),
          map((menuList: Menu) => {
            this.store
              .select(getCustomerConfig)
              .pipe(take(1))
              .subscribe(value => {
                if (value.is_product_orderer) {
                  menuList = this.updateMenuForPO(menuList);
                }
                if (value.is_financial_manager) {
                  menuList = this.updateMenuForFM(menuList);
                }
              });

            return new HomepageActions.SetMenuList({ menuList });
          })
        )
      )
    )
  });

  initGuestMenu$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<InitGuestMenuList>(MenuActionTypes.InitGuestMenuList),
      switchMap(action =>
        this.backendService
          .initGuestMenuList(action.payload.storeCode)
          .pipe(map((menuList: Menu) => new HomepageActions.SetMenuList({ menuList })))
      )
    )
  });

  updateDirectLocale$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionsTypes.SetupPermissionsSuccess),
      tap((action: SetModularityConfig) => {
        this.directLocaleService.updateCurrencyFormat(action.payload['config'].core.price_format);
      })
    )
  }, { dispatch: false });

  initContactConfig$ = createEffect(() => {
    return combineLatest([
      this.actions$.pipe(ofType(MenuActionTypes.SetMenuList)),
      this.store.pipe(select(__config_contact))]
    ).pipe(
      take(1),
      map(([_, contact]) => new HomepageActions.SetContactConfig({ contactInfo: contact.config }))
    )
  });

  initSsoLinks$ = createEffect(() => {
    return combineLatest([
      this.actions$.pipe(ofType(ActionsTypes.InitSsoLinks)),
      this.store.pipe(select(getMenuListState))]
    ).pipe(
      filter(([_, menu]) => !menu.isLoading),
      map(([_, menu]) => {
        const sso = menu['menuList'].footer
          .find(item => item.code === 'business')
          .children.filter(item => item.item_type === 'external_link');

        return new HomepageActions.SetSsoLinks({ ssoLinks: sso });
      })
    )
  });

  getCartWidgetData$ = createEffect(() => {
    return combineLatest([
      this.actions$.pipe(ofType(CartWidgetsActionTypes.GetCartWidgetData)),
      this.store.pipe(select(customerConfig))]
    ).pipe(
      filter(([, /* action */ customer]) => customer.customerConfig.contact_id !== undefined),
      switchMap(([, /* action */ customer]) =>
        this.backendService
          .getCartItems(
            sessionStorage.getItem('preferredStore'),
            customer.customerConfig.is_decision_maker || customer.customerConfig.is_product_orderer
          )
          .pipe(
            map(
              cartItems =>
                new SetCartWidgetData({
                  items: cartItems,
                  dm: customer.customerConfig.is_decision_maker || customer.customerConfig.is_product_orderer
                })
            )
          )
      )
    )
  });

  initWindowMessages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MenuActionTypes.SetMenuList),
      take(1),
      tap(() => {
        this.windowCommunicatorService.init();
        this.windowCommunicatorService.observe([BookMessageTypes.CREATE_QUOTATION]).subscribe(message => {
          if (!this.router.isActive('/book', false)) {
            // for now just notify customer, redirect is to be considered as improvement
            // this.router.navigate(['/book'], { queryParams: { quotation: true } });
            this.windowCommunicatorService.postMessage({
              id: message.id,
              type: ParserMessageTypes.PIN_STATUS,
              data: false
            });
          }
        });
        this.windowCommunicatorService.observe([ParserMessageTypes.PIN_STATUS]).subscribe(message => {
          this.windowCommunicatorService.postMessage(message);
        });
      })
    )
  }, { dispatch: false });

  windowCommunicatorService: WindowCommunicatorService;
  backendService: BackendService;
  localeMapperService: LocaleMapperService;
  directLocaleService: DirectLocaleService;
  permissionHelper: PermissionHelper;
  alert: HeaderAlertService;
  constructor(
    private readonly actions$: Actions,
    private readonly injector: Injector,
    private readonly store: Store<State>,
    private readonly router: Router,
    private readonly initService: ConfigService,
    @Optional() @Inject(MENU_FILTERS) private readonly menuFilters: IMenuFilter[]
  ) {
    this.windowCommunicatorService = injector.get<WindowCommunicatorService>(WindowCommunicatorService);
    this.backendService = injector.get<BackendService>(BackendService);
    this.localeMapperService = injector.get<LocaleMapperService>(LocaleMapperService);
    this.directLocaleService = injector.get<DirectLocaleService>(DirectLocaleService);
    this.permissionHelper = injector.get<PermissionHelper>(PermissionHelper);
    this.alert = injector.get<HeaderAlertService>(HeaderAlertService);
  }

  /**
   * Updates menu item list and adds invoices  as new area for FM
   * @param menulist is the menulist array coming from backend
   * @returns the updated menulist for financial manager
   */

  public updateMenuForFM(menulist) {
    const codeInvoices = 'account/invoices';
    const itemTypeFM = 'code';
    const title = 'Invoices';
    const account = 'account';
    const accountAgency = 'account/agency';

    const invoicesMenuObj: Item = {
      children: [],
      cms_page_code: '',
      code: codeInvoices,
      item_type: itemTypeFM,
      external_link: '',
      title
    };

    const accountIndex = menulist.navigation.findIndex(x => x.code === account);
    const accountFooterIndex = menulist.footer.findIndex(x => x.code === account);
    const agencyIndex = menulist.navigation[accountIndex].children.findIndex(x => x.code === accountAgency);

    if (menulist.navigation[accountIndex].children.findIndex(x => x.code === codeInvoices) < 0) {
      menulist.navigation[accountIndex].children.push(invoicesMenuObj);
    }

    if (menulist.footer[accountFooterIndex].children.findIndex(x => x.code === codeInvoices) < 0) {
      const indexToInsertOn = agencyIndex >= 0 ? agencyIndex : menulist.navigation[accountIndex].children.length - 1;
      menulist.footer[accountFooterIndex].children.splice(indexToInsertOn + 1, 0, invoicesMenuObj);
    }
    return menulist;
  }

  /**
   * Updates menu item list and add orders and agency wishlists as new areas for PO
   * @param menulist is the menulist array coming from backend
   * @returns the updated menulist for product orderer
   */

  public updateMenuForPO(menulist) {
    const codeOrders = 'account/orders';
    const itemTypePO = 'code';
    const title = 'Orders';
    const agencyWishlist = 'Agency wishlists';
    const agencyWishlistFooter = 'My Agency wishlists';
    const account = 'account';
    const accountWishlist = 'account/wishlists';
    const accountAgency = 'account/agency';

    const ordersMenuObj: Item = {
      children: [],
      cms_page_code: '',
      code: codeOrders,
      item_type: itemTypePO,
      external_link: '',
      title
    };
    const accountIndex = menulist.navigation.findIndex(x => x.code === account);
    const wishListIndex = menulist.navigation[accountIndex].children.findIndex(x => x.code === accountWishlist);
    const accountFooterIndex = menulist.footer.findIndex(x => x.code === account);
    const wishlistFooterIndex = menulist.footer[accountFooterIndex].children.findIndex(x => x.code === accountWishlist);
    const agencyIndex = menulist.navigation[accountIndex].children.findIndex(x => x.code === accountAgency);

    if (wishListIndex >= 0) {
      menulist.navigation[accountIndex].children[wishListIndex].title = agencyWishlist;
    }
    if (wishlistFooterIndex >= 0) {
      menulist.footer[accountFooterIndex].children[wishlistFooterIndex].title = agencyWishlistFooter;
    }
    if (menulist.navigation[accountIndex].children.findIndex(x => x.code === codeOrders) < 0) {
      const indexToInsertOn = agencyIndex >= 0 ? agencyIndex : menulist.navigation[accountIndex].children.length - 1;
      menulist.navigation[accountIndex].children.splice(indexToInsertOn + 1, 0, ordersMenuObj);
    }
    if (menulist.footer[accountFooterIndex].children.findIndex(x => x.code === codeOrders) < 0) {
      menulist.footer[accountFooterIndex].children.push(ordersMenuObj);
    }
    return menulist;
  }

  private showAgreementBanner(daysLeft: number, storeCode: number) {
    const environmentInjector = this.injector.get(EnvironmentInjector);
    const component = createComponent(AgreementBannerComponent, { environmentInjector });
    component.instance.days = daysLeft;
    component.instance.link = `${this.initService.getEnvConfig().AD_MAGENTO_URL}amadeuscms/agreement/pdf?___store=${storeCode}`;
    component.hostView.detectChanges();
    this.alert.add({
      type: 'warning',
      content: component.location.nativeElement.innerHTML,
      closable: false,
      endTime: Date.now() + 1000 * 60 * 5
    });
    component.destroy();
  }
}
